import { get, isEmpty, isNull } from 'lodash/fp';
import { useCallback } from 'react';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { COLORS, FONT_SIZES, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import { ColorInput } from 'dashboard/components/FormControls/FormControl';
import ThemeEditorFormActionFooter, {
  ACTION_FOOTER_HEIGHT,
} from 'dashboard/components/ThemeEditorFormActionFooter';
import { FORM_THEME_EDITOR_COLOR_NAME } from 'dashboard/lib/constants/formNames';
import { isColorContrastHighEnough } from 'lib/utils/formValidation';

const Title = styled('h2')`
  color: ${COLORS.black};

  padding-top: ${SPACINGS.base * 5}px;
  padding-left: ${SPACINGS.base * 2}px;
  padding-bottom: ${SPACINGS.base * 3}px;
  border-bottom: 1px solid ${COLORS.gray20};
`;
const Section = styled('div')`
  padding: ${SPACINGS.base * 2}px;
  margin-bottom: ${ACTION_FOOTER_HEIGHT};
`;
const StyledLabel = styled('label')`
  display: inline-block;
`;
const Hint = styled('p')`
  font-size: ${FONT_SIZES.small};
  color: ${COLORS.gray50};
  margin-bottom: ${SPACINGS.base}px;
`;

const StyledField = styled(Field)<any>`
  margin-bottom: ${SPACINGS.base * 3}px;
`;

interface FormThemeEditorColorProps {
  defaultValues: Record<string, string>;
  disabled?: boolean;
}

const WCAGWarning = ({ warningText }: { warningText: string }) => (
  <div>
    {`${warningText} `}
    <a
      href='https://www.w3.org/WAI/WCAG21/quickref/?versions=2.0#contrast-minimum'
      target='_blank'
      rel='noreferrer'
    >
      WCAG compliant.
    </a>
  </div>
);

const createPostfix = (texts: string[]) =>
  texts.length === 1
    ? `${texts[0]}`
    : texts.length === 2
      ? `${texts[0]} and ${texts[1]}`
      : `${texts.slice(0, texts.length - 1).join(', ')}, and ${texts[texts.length - 1]}`;

export const createWarningText = (
  theme: Record<string, string>,
  attributeDetails: Array<[string, string]>,
) => {
  const readableAttributes = attributeDetails.map(([_, readableAttribute]) => readableAttribute);

  // If the theme attribute is null, undefined, or '', isColorContrastHighEnough will still
  // return true, even though we cannot be confident that will be sufficient with client defaults.
  const comparedAgainstEmptyThemeValues = attributeDetails
    .map(([attributeKey, _]) => attributeKey)
    .map((attributeKey) => get(attributeKey, theme))
    .filter(isEmpty);

  return comparedAgainstEmptyThemeValues.length > 0
    ? `Warning: Contrast with ${createPostfix(readableAttributes)} may not be`
    : `Warning: Contrast with ${createPostfix(readableAttributes)} is not`;
};

const fieldNameToAttribute = (fieldName: string): string => fieldName.split('.')[1];

export const withComponentWCAGWarning = (
  hexColor: string,
  theme: Record<string, string>,
  defaultValues: Record<string, string>,
  name: string,
  attributeDetails: Array<[string, string]>,
) => {
  if (theme) {
    const conflictingAttributes = attributeDetails.filter(([themeAttribute]) => {
      // Check to see if either of the colors have been cleared
      if (theme[themeAttribute] === '' || hexColor === '') {
        return false;
      }
      // If neither of the colors have been cleared, we can rely on both the current field values and
      // provided values, using default values asValueOf the fallback.
      else {
        return !isColorContrastHighEnough(theme[themeAttribute] ?? defaultValues[themeAttribute])(
          hexColor ?? defaultValues[fieldNameToAttribute(name)],
        );
      }
    });

    return conflictingAttributes.length > 0 ? (
      <WCAGWarning warningText={createWarningText(theme, conflictingAttributes)} />
    ) : null;
  }
};

export const formatter =
  (attribute: string, defaultValues: Record<string, string>) => (value?: string | null | '') =>
    // Template attribute is null
    isNull(value)
      ? // Undo all pressed
        (defaultValues[attribute] ?? '')
      : // Field cleared
        typeof value === 'undefined'
        ? ''
        : value;

type WarnCallback = (
  hexColor: string,
  allValues: { theme: Record<string, string> },
  props: unknown,
  name: string,
) => JSX.Element | null | undefined;

const FormThemeEditorColor = ({
  defaultValues,
  disabled,
  reset,
  submitting,
  pristine,
  handleSubmit,
}: FormThemeEditorColorProps & InjectedFormProps) => {
  const warnForBackgroundColor = useCallback<WarnCallback>(
    (hexColor, { theme }, _, name) =>
      withComponentWCAGWarning(hexColor, theme, defaultValues, name, [
        ['color-modal-background', 'Background color in advanced settings'],
      ]),
    [defaultValues],
  );

  const warnForPrimaryButtonColor = useCallback<WarnCallback>(
    (hexColor, { theme }, _, name) =>
      withComponentWCAGWarning(hexColor, theme, defaultValues, name, [
        ['color-button-primary', 'Primary button fill color'],
      ]),
    [defaultValues],
  );

  const warnForFontColor = useCallback<WarnCallback>(
    (hexColor, { theme }, _, name) =>
      withComponentWCAGWarning(hexColor, theme, defaultValues, name, [
        ['color-font', 'Font color'],
      ]),
    [defaultValues],
  );

  return (
    <form onSubmit={handleSubmit}>
      <Title>Color</Title>
      <Section>
        <StyledLabel htmlFor='color-theme-form__color-primary-input'>Primary color</StyledLabel>
        <Hint>Brand color for buttons, links, and pictographs</Hint>
        <StyledField
          id='color-theme-form__color-primary-input'
          warn={warnForBackgroundColor}
          name='theme.color-primary'
          component={ColorInput}
        />
        <StyledLabel htmlFor='color-theme-form__icon-color-primary-input'>
          Icon fill color
        </StyledLabel>
        <Hint>Fill color for pictographs</Hint>
        <StyledField
          id='color-theme-form__icon-color-primary-input'
          warn={warnForBackgroundColor}
          name='theme.icon-color-primary'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__icon-color-stroke-input'>
          Icon stroke color
        </StyledLabel>
        <Hint>Stroke color for pictographs</Hint>
        <StyledField
          id='color-theme-form__icon-color-stroke-input'
          warn={warnForBackgroundColor}
          name='theme.icon-color-stroke'
          component={ColorInput}
        />

        {/*
        <StyledLabel>Icon highlight color</StyledLabel>
        <Hint>Accent color for certain pictographs</Hint>
        <StyledField
          warn={warnForBackgroundColor}
          name='theme.icon-color-highlight'
          component={ColorInput}
        />

        <StyledLabel>Icon background color</StyledLabel>
        <Hint>Background color for certain pictographs</Hint>
        <StyledField
          warn={warnForBackgroundColor}
          name='theme.icon-color-background'
          component={ColorInput}
        />
        */}

        <StyledLabel htmlFor='color-theme-form__color-success-input'>Success color</StyledLabel>
        <Hint>Color for success icon</Hint>
        <StyledField
          id='color-theme-form__color-success-input'
          warn={warnForBackgroundColor}
          name='theme.color-success'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-progress-bar-input'>
          Progress bar color
        </StyledLabel>
        <Hint>Color for progress bar</Hint>
        <StyledField
          id='color-theme-form__color-progress-bar-input'
          warn={warnForBackgroundColor}
          name='theme.color-progress-bar'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-font-title-input'>
          Heading text color
        </StyledLabel>
        <Hint>Text color for headings</Hint>
        <StyledField
          id='color-theme-form__color-font-title-input'
          warn={warnForBackgroundColor}
          name='theme.color-font-title'
          component={ColorInput}
          format={formatter('color-font-title', defaultValues)}
          defaultValue={defaultValues['color-font-title']}
        />

        <StyledLabel htmlFor='color-theme-form__color-font-input'>Body text color</StyledLabel>
        <Hint>Text color for body text</Hint>
        <StyledField
          id='color-theme-form__color-font-input'
          warn={warnForBackgroundColor}
          name='theme.color-font'
          component={ColorInput}
          format={formatter('color-font', defaultValues)}
          defaultValue={defaultValues['color-font']}
        />

        <StyledLabel htmlFor='color-theme-form__color-font-small-input'>
          Small text color
        </StyledLabel>
        <Hint>Text color for disclaimers, etc.</Hint>
        <StyledField
          id='color-theme-form__color-font-small-input'
          warn={warnForBackgroundColor}
          name='theme.color-font-small'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-button-primary-input'>
          Primary button fill color
        </StyledLabel>
        <Hint>Color for the primary buttons</Hint>
        <StyledField
          id='color-theme-form__color-button-primary-input'
          warn={warnForBackgroundColor}
          name='theme.color-button-primary'
          component={ColorInput}
          format={formatter('color-button-primary', defaultValues)}
          defaultValue={defaultValues['color-button-primary']}
        />

        <StyledLabel htmlFor='color-theme-form__color-font-button-primary-input'>
          Primary button text color
        </StyledLabel>
        <Hint>Text color for the primary buttons</Hint>
        <StyledField
          id='color-theme-form__color-font-button-primary-input'
          warn={warnForPrimaryButtonColor}
          name='theme.color-font-button-primary'
          component={ColorInput}
          format={formatter('color-font-button-primary', defaultValues)}
          defaultValue={defaultValues['color-font-button-primary']}
        />

        <StyledLabel htmlFor='color-theme-form__color-button-secondary-input'>
          Secondary button outline color
        </StyledLabel>
        <Hint>Outline color for the secondary buttons</Hint>
        <StyledField
          id='color-theme-form__color-button-secondary-input'
          warn={warnForBackgroundColor}
          name='theme.color-button-secondary'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-button-secondary-fill-input'>
          Secondary button fill color
        </StyledLabel>
        <Hint>Fill color for the secondary buttons</Hint>
        <StyledField
          id='color-theme-form__color-button-secondary-fill-input'
          warn={warnForBackgroundColor}
          name='theme.color-button-secondary-fill'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-font-button-secondary-input'>
          Secondary button text color
        </StyledLabel>
        <Hint>Text color for the secondary buttons</Hint>
        <StyledField
          id='color-theme-form__color-font-button-secondary-input'
          warn={warnForBackgroundColor}
          name='theme.color-font-button-secondary'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-link-input'>Link color</StyledLabel>
        <Hint>Color for links</Hint>
        <StyledField
          id='color-theme-form__color-link-input'
          warn={warnForBackgroundColor}
          name='theme.color-link'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-input-background-input'>
          Input background color
        </StyledLabel>
        <Hint>Background color for inputs and dropdown values</Hint>
        <StyledField
          id='color-theme-form__color-input-background-input'
          warn={warnForFontColor}
          name='theme.color-input-background'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-input-border-input'>
          Input border color
        </StyledLabel>
        <Hint>Border color for inputs and dropdown values</Hint>
        <StyledField
          id='color-theme-form__color-input-border-input'
          warn={warnForFontColor}
          name='theme.color-input-border'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-dropdown-input'>
          Dropdown background color
        </StyledLabel>
        <Hint>Background color for dropdown menus</Hint>
        <StyledField
          id='color-theme-form__color-dropdown-input'
          warn={warnForFontColor}
          name='theme.color-dropdown-background'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-dropdown-option-input'>
          Dropdown option color
        </StyledLabel>
        <Hint>Background color for active dropdown options</Hint>
        <StyledField
          id='color-theme-form__color-dropdown-option-input'
          warn={warnForFontColor}
          name='theme.color-dropdown-option'
          component={ColorInput}
        />

        <StyledLabel htmlFor='color-theme-form__color-icon-header-input'>
          Header button color
        </StyledLabel>
        <Hint>Icon color for back and close buttons</Hint>
        <StyledField
          id='color-theme-form__color-icon-header-input'
          warn={warnForBackgroundColor}
          name='theme.color-icon-header'
          component={ColorInput}
        />
      </Section>
      {!disabled && (
        <ThemeEditorFormActionFooter
          dataTestBlock='form-theme-editor-color'
          handleUndo={reset}
          submitting={submitting}
          pristine={pristine}
        />
      )}
    </form>
  );
};

export default reduxForm<{}, FormThemeEditorColorProps>({
  form: FORM_THEME_EDITOR_COLOR_NAME,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
})(FormThemeEditorColor);
